import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type EventDetail as Ev, type Member, type PersonRef } from '../lib/api';
import { DISCIPLINES, TYPES, ago, dLong, dShort, eventEnd, hm, rel, ymd } from '../lib/domain';
import { md } from '../lib/md';
import { PersonPicker } from '../fields';
import { CloseButton, Avatar, DiscIcon, ErrorBox, Icon, Loading } from '../ui';
import { useMe, useToast } from '../hooks';
import { dc } from '../lib/style';
import { useBarTitle } from '../barTitle';

function People({
  list,
  ev,
  editable,
  onRemove,
}: {
  list: PersonRef[];
  ev: Ev;
  editable: boolean;
  onRemove: (id: number) => void;
}) {
  return (
    <ul className="people">
      {list.map((p, i) => (
        <li key={`${p.id}-${i}`} style={{ display: 'flex', alignItems: 'center' }}>
          <span className="person">
            <span className="pos tnum">{i + 1}.</span>
            <Avatar person={p} />
            <span className="nm">{p.name}</span>
            {p.id === ev.organizer?.id && <em>Orga</em>}
          </span>
          {editable && p.id && p.id !== ev.organizer?.id && (
            <button
              className="icon-btn"
              style={{ width: 30, height: 30, border: 0 }}
              aria-label={`${p.name} austragen`}
              onClick={() => onRemove(p.id!)}
            >
              <Icon name="x" sw={2} />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function EventDetail() {
  useBarTitle('Termin');
  const id = Number(useParams().id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useMe();
  const { data: e, isLoading, error } = useQuery({ queryKey: ['event', id], queryFn: () => api<Ev>(`/events/${id}`) });
  const [confirm, setConfirm] = useState<'' | 'cancel' | 'delete'>('');
  const [comment, setComment] = useState('');
  const [notifyAll, setNotifyAll] = useState(false);
  const [addId, setAddId] = useState<number | null>(null);
  const members = useQuery({ queryKey: ['members'], queryFn: () => api<Member[]>('/members'), enabled: !!e?.can_edit });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['event', id] });
    void qc.invalidateQueries({ queryKey: ['events'] });
  };
  const onErr = (err: unknown) => toast((err as Error).message);

  const rsvp = useMutation({
    mutationFn: (status: string) => api(`/events/${id}/rsvp`, { method: 'PUT', body: { status } }),
    onSuccess: refresh,
    onError: onErr,
  });
  const setPart = useMutation({
    mutationFn: ({ uid, status }: { uid: number; status: string }) =>
      api(`/events/${id}/participants/${uid}`, { method: 'PUT', body: { status } }),
    onSuccess: refresh,
    onError: onErr,
  });
  const cancel = useMutation({
    mutationFn: (cancelled: boolean) => api(`/events/${id}/cancel`, { method: 'POST', body: { cancelled } }),
    onSuccess: (_d, c) => {
      setConfirm('');
      refresh();
      toast(c ? 'Termin abgesagt – alle werden benachrichtigt' : 'Termin findet wieder statt');
    },
    onError: onErr,
  });
  const del = useMutation({
    mutationFn: () => api(`/events/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      toast('Termin gelöscht');
      void nav('/');
    },
    onError: onErr,
  });
  const addComment = useMutation({
    mutationFn: () => api(`/events/${id}/comments`, { method: 'POST', body: { body: comment, notify_all: notifyAll } }),
    onSuccess: () => {
      setComment('');
      if (notifyAll) toast('Kommentar an alle Angemeldeten geschickt');
      setNotifyAll(false);
      refresh();
    },
    onError: onErr,
  });
  const delComment = useMutation({
    mutationFn: (cid: number) => api(`/comments/${cid}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: onErr,
  });

  if (isLoading) return <Loading />;
  if (error || !e) return <ErrorBox error={error} />;
  const start = new Date(e.starts_at),
    end = eventEnd(e);
  const past = e.past;
  const multi = e.ends_at && ymd(new Date(e.ends_at)) !== ymd(start);
  const when = multi
    ? `${dShort(start)} ${hm(start)} – ${dShort(end)} ${hm(end)} Uhr`
    : `${dLong(start)} · ${e.duration_min || e.ends_at ? `${hm(start)}–${hm(end)}` : hm(start)} Uhr`;
  const inList = new Set([...e.confirmed, ...e.waiting, ...e.maybe].map(p => p.id));

  return (
    <article className="drawer-page" style={dc(e.discipline)}>
      <div className="d-head">
        <CloseButton to="/" label="Zurück zur Liste" />
        <DiscIcon disc={e.discipline} size="lg" />
        <p className="eyebrow">
          {DISCIPLINES[e.discipline]} · {TYPES[e.type]}
          {e.series_id ? ' · Serie' : ''}
        </p>
        <h1 className="d-title">{e.title}</h1>
        <p className="d-when">
          {when}
          {!past && <span className="rel">{rel(start)}</span>}
        </p>
      </div>
      <div className="d-body">
        {e.cancelled && <div className="banner">Dieser Termin wurde abgesagt.</div>}
        {!past && !e.cancelled && (
          <>
            <div className="seg seg-rsvp" role="group" aria-label="Deine Teilnahme">
              {(
                [
                  ['yes', 'Dabei'],
                  ['maybe', 'Vielleicht'],
                  ['none', 'Nicht dabei'],
                ] as const
              ).map(([k, l]) => (
                <button
                  key={k}
                  aria-pressed={k === e.my_status || (k === 'yes' && e.my_status === 'wait')}
                  disabled={rsvp.isPending}
                  onClick={() => rsvp.mutate(k)}
                >
                  {l}
                </button>
              ))}
            </div>
            {e.my_status === 'wait' && (
              <p className="note warn">
                Ausgebucht – du stehst auf der Warteliste und rückst automatisch nach, wenn jemand absagt.
              </p>
            )}
          </>
        )}
        {past && <p className="note">Dieser Termin ist vorbei.</p>}
        <dl className="facts">
          {e.location && (
            <div>
              <dt>Ort</dt>
              <dd>
                {e.location} ·{' '}
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Karte
                </a>
              </dd>
            </div>
          )}
          {e.meeting_point && (
            <div>
              <dt>Treffpunkt</dt>
              <dd>{e.meeting_point}</dd>
            </div>
          )}
          {e.organizer && (
            <div>
              <dt>Organisation</dt>
              <dd>
                {e.organizer.name}
                {e.organizer.id === me?.id ? ' (du)' : ''}
              </dd>
            </div>
          )}
          {e.capacity && (
            <div>
              <dt>Plätze</dt>
              <dd>
                {e.confirmed.length} / {e.capacity}
                {e.waiting.length
                  ? ` · ${e.waiting.length} auf Warteliste`
                  : e.confirmed.length >= e.capacity
                    ? ' · ausgebucht'
                    : ` · ${e.capacity - e.confirmed.length} frei`}
              </dd>
            </div>
          )}
          {e.updated_at !== e.created_at && (
            <div>
              <dt>Geändert</dt>
              <dd>{ago(new Date(e.updated_at))}</dd>
            </div>
          )}
        </dl>
        {e.can_edit && (
          <div>
            {me && e.organizer_id !== me.id && (
              <p className="note admin-note">
                <Icon name="shield" sw={1.8} />
                Du kannst diesen Termin als Admin bearbeiten – angelegt hat ihn {e.organizer?.name ?? 'jemand anderes'}.
              </p>
            )}
            <div className="d-actions">
              <Link className="btn btn-navy btn-sm" to={`/termine/${e.id}/bearbeiten`}>
                <Icon name="edit" sw={1.8} />
                Bearbeiten
              </Link>
              <Link className="btn btn-ghost btn-sm" to={`/termine/neu?vorlage=${e.id}`}>
                <Icon name="copy" sw={1.8} />
                Duplizieren
              </Link>
              {!past && (
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirm('cancel')}>
                  <Icon name="ban" sw={1.8} />
                  {e.cancelled ? 'Wieder ansetzen' : 'Absagen'}
                </button>
              )}
              <button className="btn btn-danger btn-sm" onClick={() => setConfirm('delete')}>
                <Icon name="trash" sw={1.8} />
                Löschen
              </button>
            </div>
            {confirm === 'cancel' && (
              <div className="confirm" role="alert">
                <p>
                  {e.cancelled
                    ? 'Termin wieder ansetzen? Alle bekommen eine Nachricht.'
                    : 'Termin absagen? Alle Mitglieder werden sofort benachrichtigt.'}
                </p>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirm('')}>
                  Abbrechen
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => cancel.mutate(!e.cancelled)}>
                  {e.cancelled ? 'Wieder ansetzen' : 'Absagen'}
                </button>
              </div>
            )}
            {confirm === 'delete' && (
              <div className="confirm" role="alert">
                <p>Termin endgültig löschen? Zusagen und Kommentare gehen verloren.</p>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirm('')}>
                  Abbrechen
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => del.mutate()}>
                  Löschen
                </button>
              </div>
            )}
          </div>
        )}
        {e.description && (
          <section>
            <h2 className="sec-h">Beschreibung</h2>
            <div className="desc" dangerouslySetInnerHTML={{ __html: md(e.description) }} />
          </section>
        )}
        <section>
          <h2 className="sec-h">
            Teilnehmer{' '}
            <span>
              {e.confirmed.length}
              {e.capacity ? ` von ${e.capacity}` : ''}
            </span>
          </h2>
          {e.confirmed.length ? (
            <People
              list={e.confirmed}
              ev={e}
              editable={e.can_edit && !past}
              onRemove={uid => setPart.mutate({ uid, status: 'none' })}
            />
          ) : (
            <p className="note">Noch keine Zusagen.</p>
          )}
          {e.waiting.length > 0 && (
            <>
              <h3 className="sub-h">Warteliste</h3>
              <People
                list={e.waiting}
                ev={e}
                editable={e.can_edit && !past}
                onRemove={uid => setPart.mutate({ uid, status: 'none' })}
              />
            </>
          )}
          {e.maybe.length > 0 && (
            <>
              <h3 className="sub-h">Vielleicht</h3>
              <People
                list={e.maybe}
                ev={e}
                editable={e.can_edit && !past}
                onRemove={uid => setPart.mutate({ uid, status: 'none' })}
              />
            </>
          )}
          {e.can_edit && !past && (
            <div className="cform" style={{ marginTop: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <PersonPicker
                  people={(members.data || []).filter(m => !inList.has(m.id))}
                  value={addId}
                  onChange={setAddId}
                  label="Person eintragen"
                  placeholder="Person eintragen: Name eintippen …"
                  hideLabel
                />
              </div>
              <button
                className="btn btn-ghost btn-sm"
                disabled={!addId}
                onClick={() => {
                  if (addId) setPart.mutate({ uid: addId, status: 'yes' });
                  setAddId(null);
                }}
              >
                Eintragen
              </button>
            </div>
          )}
        </section>
        <section>
          <h2 className="sec-h">
            Kommentare <span>{e.comments.length}</span>
          </h2>
          {e.comments.length > 0 && (
            <ul className="comments">
              {e.comments.map(c => (
                <li key={c.id}>
                  <Avatar person={c.user} />
                  <div>
                    <b>{c.user.name}</b> <small>{ago(new Date(c.created_at))}</small>
                    {c.can_delete && (
                      <>
                        {' '}
                        <button className="linkbtn" style={{ fontSize: 12 }} onClick={() => delComment.mutate(c.id)}>
                          löschen
                        </button>
                      </>
                    )}
                    <p>{c.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <form
            className="cform"
            onSubmit={ev => {
              ev.preventDefault();
              if (comment.trim()) addComment.mutate();
            }}
          >
            <input
              maxLength={2000}
              value={comment}
              onChange={ev => setComment(ev.target.value)}
              placeholder="Frage, Mitfahrgelegenheit …"
              aria-label="Kommentar schreiben"
            />
            <button className="btn btn-primary btn-sm" disabled={addComment.isPending || !comment.trim()}>
              Senden
            </button>
          </form>
          {e.can_edit && (
            <label className="note" style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={notifyAll} onChange={ev => setNotifyAll(ev.target.checked)} />
              An alle Angemeldeten schicken (E-Mail an alle, die zugesagt haben oder auf der Warteliste stehen)
            </label>
          )}
          <p className="note">
            {e.can_edit
              ? 'Wer hier kommentiert, bekommt deine Antworten per E-Mail.'
              : 'Die Person, die den Termin angelegt hat, bekommt deinen Kommentar per E-Mail – und du ihre Antwort.'}
          </p>
        </section>
      </div>
    </article>
  );
}
